import Form from 'react-bootstrap/Form'
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useState, useEffect } from 'react'
import { format, parse } from 'date-fns';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import Select from 'components/general/ViewportSelect';
import { getSocket } from '../../../socket';
import { getContestPluginConfig } from '../../../services/contestPlugin'

export default function MeetingEditForm({ meeting, getMeetings = null, onCancel, formId = 'meeting-edit-form' }) {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [meetingTime, setMeetingTime] = useState('')
  const [meetingMode, setMeetingMode] = useState('inPerson')
  const [meetingAddress, setMeetingAddress] = useState('')
  const [meetingOnlineLink, setMeetingOnlineLink] = useState('')
  const [meetingOnlinePasscode, setMeetingOnlinePasscode] = useState('')
  const [meetingNumber, setMeetingNumber] = useState('')
  const [meetingTheme, setMeetingTheme] = useState(meeting?.meetingTheme || '')
  const [meetingCardDisplayRole, setMeetingCardDisplayRole] = useState(meeting?.meetingCardDisplayRole || 'Toastmaster')
  const [agendaTemplates, setAgendaTemplates] = useState([])
  const [meetingRoles, setMeetingRoles] = useState([])
  const [agendaTemplate, setAgendaTemplate] = useState('')
  const [contestPluginEnabled, setContestPluginEnabled] = useState(false)
  const [isContest, setIsContest] = useState(false)
  const [showClockText, setShowClockText] = useState(true)
  const [contestTypesSelected, setContestTypesSelected] = useState([])
  const [contestTypes, setContestTypes] = useState([])
  const [theme, setTheme] = useState(document.body.getAttribute('data-theme') || 'light')

  const url = process.env.REACT_APP_URL;
  const { user: authUser } = useSelector(x => x.auth)

  useEffect(() => {
    if (meeting) {
      const parsedDate = parse(meeting.meetingDate, 'dd/MM/yyyy', new Date());
      setSelectedDate(parsedDate || new Date());
      setMeetingTime(meeting.meetingTime || '');
      setMeetingMode(meeting.meetingMode || 'inPerson');
      setMeetingAddress(meeting.meetingAddress || '');
      setMeetingOnlineLink(meeting.meetingOnlineLink || '');
      setMeetingOnlinePasscode(meeting.meetingOnlinePasscode || '');
      setMeetingNumber(meeting.meetingNumber || '');
      setMeetingTheme(meeting.meetingTheme || '');
      setMeetingCardDisplayRole(meeting.meetingCardDisplayRole || 'Toastmaster')
      setAgendaTemplate(meeting.agendaTemplate || '')
      setIsContest(Boolean(meeting.isContest))
      setShowClockText(meeting.showClockText !== false)
      setContestTypesSelected(Array.isArray(meeting.contestTypes) ? meeting.contestTypes : meeting.contestType ? [meeting.contestType] : [])
    }
  }, [meeting]);

  useEffect(() => {
    if (!authUser?.token) return
    getContestPluginConfig(authUser.token).then(config => { setContestPluginEnabled(config.enabled); setContestTypes(config.contestTypes); }).catch(() => { setContestPluginEnabled(false); setContestTypes([]); })
  }, [authUser?.token])

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.body.getAttribute('data-theme') || 'light')
    })
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    fetch(`${url}/agendatemplates`, {
      headers: {
        'Authorization': `Bearer ${authUser.token}`
      }
    })
      .then(res => res.json())
      .then(data => setAgendaTemplates(Array.isArray(data) ? data : []))
      .catch(() => setAgendaTemplates([]))
  }, [url, authUser.token])

  useEffect(() => {
    fetch(`${url}/meetingRoles`, { method: 'GET' })
      .then(res => res.json())
      .then(data => setMeetingRoles(Array.isArray(data) ? data : []))
      .catch(() => setMeetingRoles([]))
  }, [url])

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#23272b' : '#fff',
      color: theme === 'dark' ? '#fff' : '#222',
      borderColor: state.isFocused ? (theme === 'dark' ? '#FFD700' : '#004165') : (theme === 'dark' ? '#444' : '#ccc'),
      boxShadow: state.isFocused ? `0 0 0 2px ${theme === 'dark' ? '#FFD700' : '#004165'}` : 'none',
      minHeight: '44px',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#23272b' : '#fff',
      color: theme === 'dark' ? '#fff' : '#222',
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 2000,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#fff' : '#222',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? (theme === 'dark' ? '#FFD700' : '#004165')
        : state.isFocused
          ? (theme === 'dark' ? 'rgba(255,215,0,0.08)' : 'rgba(0,65,101,0.08)')
          : (theme === 'dark' ? '#23272b' : '#fff'),
      color: state.isSelected
        ? (theme === 'dark' ? '#23272b' : '#fff')
        : (theme === 'dark' ? '#fff' : '#222'),
      fontWeight: state.isSelected ? 700 : 400,
    }),
    input: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#fff' : '#222',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#888',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme === 'dark' ? '#FFD700' : '#004165',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: theme === 'dark' ? '#FFD700' : '#004165',
    }),
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const formattedDate = format(selectedDate, 'dd/MM/yyyy');
      const data = {
        agendaTemplate: agendaTemplate || null,
        isContest: contestPluginEnabled && isContest,
        contestTypes: contestPluginEnabled && isContest ? contestTypesSelected : [],
        meetingDate: formattedDate,
        meetingTime,
        meetingMode,
        meetingAddress: meetingMode === 'inPerson' ? meetingAddress : '',
        meetingOnlineLink: meetingMode === 'online' ? meetingOnlineLink : '',
        meetingOnlinePasscode: meetingMode === 'online' ? meetingOnlinePasscode : '',
        meetingNumber,
        meetingTheme,
        meetingCardDisplayRole,
        showClockText
      }
      const response = await fetch(`${url}/meeting/${meeting._id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authUser.token
        },
        body: JSON.stringify(data)
      })
      if (response.ok) {
        getSocket().emit('meeting:clock-visibility', {
          meetingId: meeting._id,
          visible: showClockText,
        });
      }
      if (response.ok) {
        toast.success(`meeting ${meetingTheme} has been updated`, { position: 'bottom-left' })
        if (getMeetings) getMeetings()
        // handleCloseEditMeetingModal()
      }
      else {
        toast.error(`Failed to update the meeting ${meetingTheme}`)
      }
    }
    catch (error) {
      toast.error(`An error occurred while updating the meeting ${meetingTheme}`)
    }
    finally {
      onCancel()
    }
  }

  const roleOptions = meetingRoles
    .map((role) => ({ value: role.name, label: role.name }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <Form id={formId} onSubmit={handleSubmit} className=''>
      <Form.Group className='mb-3'>
        <Form.Label>Meeting Date</Form.Label>
        <DatePicker
          wrapperClassName='formInput'
          showIcon
          selected={selectedDate}
          onChange={(date) => setSelectedDate(date)}
          dateFormat="dd/MM/yyyy"
        />
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>Time</Form.Label>
        <Form.Control
          className='formInput'
          placeholder='Time: HH:mm'
          aria-label='Time'
          name='meetingTime'
          value={meetingTime}
          onChange={e => setMeetingTime(e.target.value)}
        />
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>Meeting Mode</Form.Label>
        <Form.Select
          aria-label='Meeting Mode'
          className='formInput'
          name='meetingMode'
          value={meetingMode}
          onChange={e => setMeetingMode(e.target.value)}
        >
          <option value='inPerson'>In Person</option>
          <option value='online'>Online</option>
        </Form.Select>
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>{meetingMode === 'online' ? 'Meeting Link' : 'Location'}</Form.Label>
        {meetingMode === 'online' ? (
          <Form.Control
            className='formInput'
            placeholder='Meeting Link'
            aria-label='Meeting Link'
            name='meetingOnlineLink'
            value={meetingOnlineLink}
            onChange={e => setMeetingOnlineLink(e.target.value)}
          />
        ) : (
          <Form.Control
            className='formInput'
            placeholder='Location'
            aria-label='Location'
            name='meetingAddress'
            value={meetingAddress}
            onChange={e => setMeetingAddress(e.target.value)}
          />
        )}
      </Form.Group>
      {meetingMode === 'online' && (
        <Form.Group className='mb-3'>
          <Form.Label>Meeting Passcode (optional)</Form.Label>
          <Form.Control
            className='formInput'
            placeholder='Passcode (optional)'
            aria-label='Meeting Passcode'
            name='meetingOnlinePasscode'
            value={meetingOnlinePasscode}
            onChange={e => setMeetingOnlinePasscode(e.target.value)}
          />
        </Form.Group>
      )}
      <Form.Group className='mb-3'>
        <Form.Label>Meeting Number</Form.Label>
        <Form.Control
          className='formInput'
          placeholder='Meeting Number'
          aria-label='meetingNo'
          name='meetingNumber'
          value={meetingNumber}
          onChange={e => setMeetingNumber(e.target.value)}
        />
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>Meeting Theme</Form.Label>
        <Form.Control
          className='formInput'
          placeholder='Meeting Theme'
          aria-label='meeting Theme'
          name='meetingTheme'
          value={meetingTheme}
          onChange={e => setMeetingTheme(e.target.value)}
        />
      </Form.Group>
      <Form.Group className='mb-3' style={{ display: 'block' }}>
        <Form.Label className='mb-2' style={{ textAlign: 'left', width: '100%' }}>
          Display role on meeting card
        </Form.Label>
        <Select
          options={roleOptions}
          value={roleOptions.find(option => option.value === meetingCardDisplayRole) || null}
          onChange={option => setMeetingCardDisplayRole(option ? option.value : 'Toastmaster')}
          styles={customStyles}
          isSearchable
          isClearable={false}
          placeholder='Select a role'
          menuPortalTarget={typeof window !== 'undefined' ? window.document.body : null}
          menuPosition='fixed'
        />
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>Agenda Template</Form.Label>
        <Form.Select
          className='formInput'
          aria-label='Agenda Template'
          name='agendaTemplate'
          value={agendaTemplate}
          onChange={e => setAgendaTemplate(e.target.value)}
        >
          <option value=''>No Agenda Template</option>
          {agendaTemplates.map(template => (
            <option key={template._id} value={template._id}>{template.name}</option>
          ))}
        </Form.Select>
      </Form.Group>
      {contestPluginEnabled && <Form.Group className='mb-3'><Form.Check type='checkbox' label='Contest meeting' checked={isContest} onChange={e => { setIsContest(e.target.checked); if (!e.target.checked) setContestTypesSelected([]); }} />{isContest && <><Form.Select className='formInput mt-2' value='' onChange={e => { if (e.target.value && !contestTypesSelected.includes(e.target.value)) setContestTypesSelected(previous => [...previous, e.target.value]); }}><option value=''>Add contest type</option>{contestTypes.map(type => <option key={type._id} value={type._id}>{type.name}</option>)}</Form.Select><div className='mt-2'>{contestTypesSelected.map(typeId => <span className='badge bg-secondary me-2' key={typeId}>{contestTypes.find(type => type._id === typeId)?.name || typeId}<button type='button' className='btn-close btn-close-white ms-2' aria-label='Remove contest type' onClick={() => setContestTypesSelected(previous => previous.filter(id => id !== typeId))} /></span>)}</div></>}</Form.Group>}
      <Form.Group className='mb-3'>
        <Form.Check
          type='checkbox'
          label='Show clock text during timer'
          checked={showClockText}
          onChange={e => setShowClockText(e.target.checked)}
        />
      </Form.Group>
    </Form>
  );
}